import type {
    MinMaxRange} from "@@/utilities/set-within-range";
import {
    minMaxRangeZodSchema,
} from "@@/utilities/set-within-range";
import { z } from "zod";
import type {
    ResourcePrice} from "../resource-price/resource-price";
import {
    resourcePriceZodSchema,
} from "../resource-price/resource-price";
import { isManifestBookableOption } from "../resource-reference-options/is-manifest-bookable-option";
import type { ResourceReference } from "../resource-references/resource-reference";
import type {
    ResourceReferenceId} from "../resource-references/resource-reference-id";
import {
    resourceReferenceIdZodSchema,
} from "../resource-references/resource-reference-id";
import type { ResourceId} from "../resources/resource-id";
import { resourceIdZodSchema } from "../resources/resource-id";
import type { ResourceLocal } from "../resources/resource-local";
import type { BookableSession } from "../sessions/bookable-session";

//Overrides options
type ResRefOptionOverride = {
    minMax?: MinMaxRange;
    resourcePrice?: ResourcePrice;
};
const resRefOptionOverrideZodSchema: z.ZodSchema<ResRefOptionOverride> =
    z.object({
        minMax: minMaxRangeZodSchema.optional(),
        resourcePrice: resourcePriceZodSchema.optional(),
    });
//Override a resource-reference
type ResourceRefOverride = {
    templateResourceRefId: ResourceReferenceId;
    resourceId?: ResourceId; //Override på resourceId / session bound
    resourceQuantity?: number; // override:ar en resource antal
    data: Record<string, ResRefOptionOverride>;
};
const resourceRefOverrideZodSchema: z.ZodSchema<ResourceRefOverride> = z.object(
    {
        templateResourceRefId: resourceReferenceIdZodSchema,
        resourceId: resourceIdZodSchema,
        resourceQuantity: z.number().optional(),
        data: z.record(z.string(), resRefOptionOverrideZodSchema),
    }
);

const getOverrideResource = (
    session: BookableSession | undefined,
    item: ResourceReference
) => {
    if (!session || item.resource._type !== "RESOURCE_LOCAL")
        return item.resource;

    const local: ResourceLocal = {
        ...item.resource,
        _id: session.mappings[item._id]?.resourceId ?? item.resource._id,
        quantity:
            session.mappings[item._id]?.resourceQuantity ??
            item.resource.quantity,
    };
    return local;
};

const getOverrideResourceIds = (
    session: BookableSession | undefined,
    item: ResourceReference
) => {
    if (!session || item.resource._type === "RESOURCE_SHARED")
        return [item.resource._id];

    if (item.resource._type === "RESOURCE_GROUP") {
        return item.resource.resources.map(f => f._id);
    }

    return [session.mappings[item._id]?.resourceId ?? item.resource._id];
};

const getOverrideResourceOptions = (
    session: BookableSession | undefined,
    item: ResourceReference
) => {
    return item.options.map(o => {
        if (isManifestBookableOption(o)) return o;

        return {
            ...o,
            resourcePrice:
                session?.mappings[item._id]?.data[o._id]?.resourcePrice ??
                o.resourcePrice,
            resourceId:
                session?.mappings[item._id]?.resourceId ?? item.resource._id,
        };
    });
};

const getOverrideResourceReference = (
    session: BookableSession | undefined,
    item: ResourceReference
) => {
    if (!session) return item;
    const overrideResource = getOverrideResource(session, item);

    return {
        ...item,
        resource: overrideResource,
        options: getOverrideResourceOptions(session, item),
    } as ResourceReference;
};

type ResourceMappings = Record<string, ResourceRefOverride>;

export type { ResourceRefOverride, ResourceMappings, ResRefOptionOverride };
export {
    getOverrideResource,
    getOverrideResourceReference,
    getOverrideResourceIds,
    resourceMappingsZodSchema,
    resourceRefOverrideZodSchema,
    resRefOptionOverrideZodSchema,
};
const resourceMappingsZodSchema: z.ZodSchema<ResourceMappings> = z.record(
    z.string(),
    resourceRefOverrideZodSchema
);
